import React from 'react';
import { MailIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useBrand } from '../../hooks/useBrand';
import type { BrandContent } from '../../data/brandContent';
interface NewsletterSectionProps {
  content: BrandContent['newsletter'];
}

/** Newsletter signup. Copy and perks are passed in; colours come from the theme. */
export const NewsletterSection: React.FC<NewsletterSectionProps> = ({
  content
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    theme
  } = useBrand();
  return <section className={`py-12 ${dk.surface}`} data-id="element-1842" data-mp="h126zwntrow2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1843" data-mp="h3wp3hjd8xmb">
        <div className={`rounded-2xl shadow-md overflow-hidden border relative ${dk.card}`} data-id="element-1844" data-mp="c3isqwr4nc44">
          {/* Decorative top border */}
          <div className={`absolute top-0 left-0 right-0 h-1.5 ${theme.newsletter.stripe}`} data-id="element-1845" data-mp="l3yvg401qf7x" />

          <div className="p-8 md:p-12 text-center" data-id="element-1846" data-mp="2r063zp23kdsh">
            <div className="flex justify-center mb-4" data-id="element-1847" data-mp="2r1h8xs7a3rl6">
              <div className={`p-3 rounded-full ${theme.newsletter.iconTile}`} data-id="element-1848" data-mp="2vzipa5dblcsy">
                <MailIcon className={`h-8 w-8 ${theme.accent.icon}`} data-id="element-1849" data-mp="30x0zdz32py2j" />
              </div>
            </div>
            <h2 className={`text-2xl md:text-3xl font-bold mb-3 ${dk.textHeading}`} data-id="element-1850" data-mp="35wx4y2rf5xk7">
              {content.title}
            </h2>
            <p className={`mb-6 max-w-2xl mx-auto ${dk.textSecondary}`} data-id="element-1851" data-mp="39weasxarcwpw">
              {content.description}
            </p>

            {/* Social proof & previews */}
            <div className="flex flex-col items-center mb-8" data-id="element-1852" data-mp="3jok0u1hg942g">
              <div className="flex -space-x-2 overflow-hidden mb-3" data-id="element-1853" data-mp="3onodj23l1jxj">
                {[1, 2, 3, 4].map(i => <img key={i} className={`inline-block h-8 w-8 rounded-full ring-2 ${dk.isDark ? 'ring-gray-800' : 'ring-white'}`} src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="" data-id="element-1854" data-mp="3tn9870bpz64g" />)}
              </div>
              <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-1855" data-mp="1tljfcr6m1nyh">
                {content.socialProof}
              </p>
              <div className={`flex flex-wrap justify-center gap-4 mt-4 text-xs font-medium ${dk.textMuted}`} data-id="element-1856" data-mp="27d7vd5jzcwkq">
                {content.perks.map(perk => <span key={perk.label} className="flex items-center" data-id="element-1857" data-mp="2hbnhsv82bhnw">
                    <span className="mr-1.5" aria-hidden="true" data-id="element-1858" data-mp="2c9y7jhsnz80v">
                      {perk.glyph}
                    </span>
                    {perk.label}
                  </span>)}
              </div>
            </div>

            <form className="max-w-md mx-auto" data-id="element-1859" data-mp="vw82o8tq0fq6">
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-1860" data-mp="vwzm6la1mn03">
                <label htmlFor="newsletter-email" className="sr-only" data-id="element-1861" data-mp="ruwbvzz41oge">
                  Email address
                </label>
                <input id="newsletter-email" type="email" placeholder="Your email address" className={`flex-grow px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 ${theme.accent.focusRing} ${dk.input}`} required data-id="element-1862" data-mp="hrqz14795b2n" />
                <button type="submit" className={`font-medium px-6 py-3 rounded-lg transition-colors ${rt.primaryButton}`} data-id="element-1863" data-mp="cy4rtrjuxze8">
                  Subscribe
                </button>
              </div>
              <p className={`text-xs mt-3 ${dk.textFaint}`} data-id="element-1864" data-mp="3l7y4ng989ie2">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>;
};